import React, { useState, useEffect } from 'react';
import { FileText, Shield, Users, BookOpen } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

// Versão dos Termos e Políticas - IMPORTANTE: Atualizar quando houver mudanças
const TERMS_VERSION = {
  termos: '1.3.0',
  contrato: '1.3.0',
  privacidade: '1.0.0',
  reembolso: '1.1.0',
  aceitavel: '1.0.0',
  dataAtualizacao: '2026-07-24T16:38:46Z'
};

const URL_TO_SECTION_MAP = {
  'termosdeservico': 'termos',
  'contratodehospedagem': 'contrato',
  'politicadeprivacidade': 'privacidade',
  'politicadereembolso': 'reembolso',
  'politicadeusoaceitavel': 'aceitavel'
};

const SECTION_TO_URL_MAP = {
  'termos': 'termosdeservico',
  'contrato': 'contratodehospedagem',
  'privacidade': 'politicadeprivacidade',
  'reembolso': 'politicadereembolso',
  'aceitavel': 'politicadeusoaceitavel'
};

function getSectionFromUrl() {
  const path = window.location.pathname;
  const cleanPath = path.replace(/\/$/, '');
  const urlParts = cleanPath.split('/').filter(part => part !== '');
  const urlPart = urlParts[urlParts.length - 1];
  return URL_TO_SECTION_MAP[urlPart] || null;
}

const LegalPage = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  // Função para atualizar a URL quando uma seção é expandida
  const updateUrl = (sectionId) => {
    if (sectionId) {
      const urlPart = SECTION_TO_URL_MAP[sectionId];
      const newUrl = `/legal/${urlPart}/#termos-politicas`;
      window.history.pushState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', '/legal');
    }
  };

  // Função para navegar para uma seção específica
  const navigateToSection = (sectionId) => {
    setExpandedSection(sectionId);
    updateUrl(sectionId);
    
    // Scroll para a seção Termos e Políticas
    setTimeout(() => {
      const element = document.getElementById('termos-politicas');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Detectar seção inicial baseada na URL
  useEffect(() => {
    const sectionFromUrl = getSectionFromUrl();
    if (sectionFromUrl) {
      setExpandedSection(sectionFromUrl);
      
      // Scroll para a seção Termos e Políticas após um pequeno delay
      setTimeout(() => {
        const element = document.getElementById('termos-politicas');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  // Monitorar mudanças na URL
  useEffect(() => {
    const handleUrlChange = () => {
      const sectionFromUrl = getSectionFromUrl();
      if (sectionFromUrl && sectionFromUrl !== expandedSection) {
        setExpandedSection(sectionFromUrl);
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [expandedSection]);

  const termosContent = (
    <div className="space-y-6">
      <div className="bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-500 border-opacity-30">
        <p className="text-purple-200 text-sm">
          <strong>Última Revisão:</strong> 24/07/2026
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">1. Aceitação dos Termos</h4>
        <p className="text-purple-200 leading-relaxed">
          Ao acessar e utilizar os serviços da Hostul (CNPJ: 56.013.570/0001-70), você concorda em cumprir e estar vinculado a estes Termos de Serviço. 
          Se você não concordar com qualquer parte destes termos, não deve utilizar nossos serviços. Estes termos constituem um acordo legal 
          entre você e a Hostul, estabelecendo as condições para utilização de todos os nossos serviços.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">2. Descrição dos Serviços</h4>
        <p className="text-purple-200 leading-relaxed">
          A Hostul oferece uma gama completa de serviços digitais, incluindo hospedagem web compartilhada e dedicada, registro e gerenciamento de domínios, 
          desenvolvimento de sites e aplicações web, soluções em nuvem (cloud hosting), painel de controle personalizado, sistema de checkout integrado, 
          processamento de pagamentos seguros, certificados SSL, backup automático e suporte técnico especializado. Nos reservamos o direito de modificar, 
          suspender ou descontinuar qualquer aspecto dos serviços a qualquer momento, sempre comunicando previamente aos clientes.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">3. Teste Gratuito de Hospedagem</h4>
        <p className="text-purple-200 leading-relaxed">
          Novos clientes elegíveis podem ativar um único teste gratuito de hospedagem por conta, com duração de 14 dias.
          O teste não exige cartão, não gera cobrança e não é convertido automaticamente em assinatura paga. Durante esse
          período, os recursos técnicos e os limites correspondentes ao plano escolhido para avaliação ficam integralmente
          disponíveis, incluindo quantidade de sites, bancos de dados, armazenamento e caixas de e-mail profissional, sempre
          sujeitos a estes Termos e à Política de Uso Aceitável. O registro ou a transferência de domínio não estão incluídos
          no teste e somente podem ser ativados após a contratação de um plano pago elegível. Se a contratação ocorrer antes
          do encerramento do teste, os dias gratuitos restantes serão preservados e o ciclo pago começará na data originalmente
          prevista para o fim do teste.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">4. Uso Aceitável e Restrições</h4>
        <p className="text-purple-200 leading-relaxed">
          Você concorda em não utilizar nossos serviços para atividades ilegais, spam, distribuição de malware, vírus ou qualquer software malicioso, 
          violação de direitos autorais ou propriedade intelectual, conteúdo pornográfico ou adulto sem verificação de idade, atividades terroristas 
          ou que promovam violência, discriminação ou assédio, phishing, fraudes online, mineração de criptomoedas não autorizada, ou qualquer atividade 
          que possa prejudicar outros usuários, a infraestrutura da Hostul ou violar leis aplicáveis.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">5. Responsabilidades do Cliente</h4>
        <p className="text-purple-200 leading-relaxed">
          Você é responsável por manter a segurança de suas credenciais de acesso, backup regular de seus dados, conformidade com todas as leis aplicáveis 
          (incluindo LGPD, Marco Civil da Internet e regulamentações específicas), conteúdo hospedado, uso adequado dos recursos contratados, manutenção 
          de sistemas atualizados, proteção contra vulnerabilidades de segurança e obtenção de todas as licenças necessárias para o conteúdo utilizado.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">6. Limitação de Responsabilidade</h4>
        <p className="text-purple-200 leading-relaxed">
          A Hostul não será responsável por danos indiretos, incidentais, consequenciais, punitivos ou especiais decorrentes do uso de nossos serviços, 
          incluindo perda de dados, lucros, receitas, interrupção de negócios, danos reputacionais, perda de oportunidades comerciais, ou qualquer 
          outro dano econômico. Nossa responsabilidade total será limitada ao valor pago pelos serviços no período em que ocorreu o problema.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">7. Propriedade Intelectual</h4>
        <p className="text-purple-200 leading-relaxed">
          Todo o conteúdo, marcas, logos, software, design, código-fonte, documentação e materiais da Hostul são protegidos por direitos autorais, 
          marcas registradas e outras leis de propriedade intelectual. O cliente mantém a propriedade de seu conteúdo original, mas concede à Hostul 
          uma licença não-exclusiva, mundial e livre de royalties para hospedar, distribuir e processar seu conteúdo conforme necessário para prestação 
          dos serviços contratados.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">8. Modificações dos Termos</h4>
        <p className="text-purple-200 leading-relaxed">
          Reservamo-nos o direito de modificar estes termos a qualquer momento para refletir mudanças em nossos serviços, práticas comerciais ou 
          requisitos legais. Alterações significativas serão comunicadas com antecedência de 30 dias através de e-mail, notificação no painel de controle 
          ou aviso em nosso site. O uso continuado dos serviços após as modificações constitui aceitação dos novos termos. Caso não concorde com as 
          alterações, você pode cancelar os serviços antes da data de vigência das modificações.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">9. Força Maior</h4>
        <p className="text-purple-200 leading-relaxed">
          A Hostul não será responsável por falhas ou atrasos na prestação dos serviços causados por eventos de força maior, incluindo mas não se 
          limitando a: desastres naturais, ataques cibernéticos, falhas de infraestrutura de terceiros, ações governamentais, greves, interrupções 
          de energia, ou qualquer outro evento fora de nosso controle razoável. Em tais situações, nos esforçaremos para restaurar os serviços 
          o mais rapidamente possível.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">10. Disposições Gerais</h4>
        <p className="text-purple-200 leading-relaxed">
          Estes termos constituem o acordo completo entre as partes e substituem todos os acordos anteriores. Se qualquer disposição destes termos 
          for considerada inválida ou inexequível, as demais disposições permanecerão em pleno vigor e efeito. A falha da Hostul em fazer valer 
          qualquer direito ou disposição destes termos não constituirá renúncia a tal direito ou disposição. Estes termos serão regidos pelas 
          leis brasileiras e qualquer controvérsia será resolvida no foro da comarca da sede da Hostul.
        </p>
      </div>
    </div>
  );

  const contratoContent = (
    <div className="space-y-6">
      <div className="bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-500 border-opacity-30">
        <p className="text-purple-200 text-sm">
          <strong>Última Revisão:</strong> 24/07/2026
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">1. Objeto do Contrato</h4>
        <p className="text-purple-200 leading-relaxed">
          Este contrato estabelece os termos e condições para a prestação de serviços de hospedagem web, registro e gerenciamento de domínios, 
          desenvolvimento de sites e aplicações web, soluções em nuvem (cloud hosting) e serviços relacionados pela Hostul (CNPJ: 56.013.570/0001-70). 
          Os serviços incluem espaço em disco, largura de banda, recursos técnicos (CPU, RAM, conexões simultâneas), ferramentas de gerenciamento, 
          painel de controle, certificados SSL, backup automático e suporte técnico conforme especificado no plano contratado.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">2. Condições do Teste Gratuito</h4>
        <p className="text-purple-200 leading-relaxed">
          O teste gratuito é disponibilizado uma única vez por conta, exclusivamente a novos clientes elegíveis, por 14
          dias consecutivos. Sua ativação não exige cartão e não autoriza cobrança automática. Durante esse período, ficam
          integralmente disponíveis os recursos técnicos e os limites do plano escolhido, incluindo quantidade de sites,
          bancos de dados, armazenamento e caixas de e-mail profissional, sujeitos ao uso aceitável. O registro ou a
          transferência de domínio não fazem parte do teste e somente são disponibilizados após a contratação do plano pago
          elegível, conforme as condições da oferta escolhida. Quando a contratação for confirmada antes do fim do teste, o
          período gratuito restante será preservado e a vigência paga começará na data originalmente prevista para o encerramento
          do teste.
        </p>
        <p className="text-purple-200 leading-relaxed mt-3">
          Se não houver contratação ao término dos 14 dias, a hospedagem será pausada automaticamente. Os dados poderão ser
          mantidos por até 30 dias para eventual recuperação e conversão para um plano pago e, depois desse prazo, poderão
          ser excluídos permanentemente. O cliente deve manter cópia própria de seus dados. Violações destes termos, abuso
          dos recursos ou atividade ilícita podem resultar em suspensão antecipada e exclusão, observadas as obrigações legais.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">3. Prazo e Renovação</h4>
        <p className="text-purple-200 leading-relaxed">
          O contrato pago tem prazo determinado conforme o ciclo escolhido no checkout e será automaticamente renovado
          por períodos iguais, salvo cancelamento expresso pelo cliente com antecedência mínima de 30 dias. A renovação automática garante 
          continuidade dos serviços e evita interrupções. O cliente pode alterar o período de renovação a qualquer momento, sendo a alteração 
          aplicada na próxima renovação.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">4. Valores e Forma de Pagamento</h4>
        <p className="text-purple-200 leading-relaxed">
          Os valores serão cobrados antecipadamente conforme o plano contratado. No checkout atual, aceitamos pagamentos via cartão de crédito
          (com parcelamento em até 12x) e PIX. Atrasos no pagamento podem resultar na suspensão dos serviços após 5 dias
          de vencimento, com cobrança de multa de 2% ao mês e juros de 1% ao mês. Após 30 dias de atraso, os serviços podem ser cancelados 
          definitivamente, com possível perda de dados.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">5. Disponibilidade e SLA</h4>
        <p className="text-purple-200 leading-relaxed">
          Nos comprometemos a manter uma disponibilidade mínima de 99,9% dos serviços. Em caso de indisponibilidade superior ao SLA, serão 
          aplicados descontos automáticos na próxima fatura: 5% para indisponibilidade entre 99,5% e 99,9%, 10% para indisponibilidade entre 
          99% e 99,5%, 25% para indisponibilidade entre 95% e 99%, e 50% para indisponibilidade abaixo de 95%. O SLA não se aplica a 
          manutenções programadas (comunicadas com 48h de antecedência) ou eventos de força maior.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">6. Suporte Técnico</h4>
        <p className="text-purple-200 leading-relaxed">
          Oferecemos suporte técnico 24/7 através de chat ao vivo, e-mail, telefone e sistema de tickets para todos os clientes. O nível de 
          suporte varia conforme o plano contratado: tempo de resposta de 2 horas para planos Premium, 4 horas para planos Standard e 8 horas 
          para planos Básicos. O suporte inclui assistência com configurações, troubleshooting, migrações e orientações técnicas, mas não 
          inclui desenvolvimento de código personalizado ou design de sites.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">7. Backup e Segurança</h4>
        <p className="text-purple-200 leading-relaxed">
          Realizamos backups automáticos semanais no plano Básico e diários nos planos Profissional e Hostul Max, conforme
          as condições apresentadas na contratação. A Hostul
          implementa medidas de segurança técnicas e organizacionais incluindo firewalls, criptografia SSL/TLS, monitoramento 24/7, controle 
          de acesso rigoroso e proteção contra DDoS. O cliente é responsável por manter backups adicionais de seus dados críticos e implementar 
          medidas de segurança em suas aplicações.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">8. Recursos e Limitações</h4>
        <p className="text-purple-200 leading-relaxed">
          Cada plano possui limites específicos de recursos: espaço em disco, largura de banda mensal, processamento de CPU, memória RAM, 
          conexões simultâneas e número de domínios. O uso excessivo que prejudique outros usuários pode resultar em throttling (redução 
          temporária de velocidade) ou suspensão temporária. O cliente pode solicitar upgrade de plano a qualquer momento para aumentar 
          os recursos disponíveis.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">9. Rescisão</h4>
        <p className="text-purple-200 leading-relaxed">
          Qualquer das partes pode rescindir este contrato mediante aviso prévio de 30 dias. Em caso de rescisão pelo cliente, será fornecido 
          um período de migração de 15 dias para backup dos dados. Após este período, os dados poderão ser excluídos permanentemente. 
          A Hostul pode rescindir imediatamente em caso de violação dos termos de uso, não pagamento ou atividades ilegais. Após a rescisão, 
          o cliente não terá mais acesso aos serviços ou dados.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">10. Confidencialidade</h4>
        <p className="text-purple-200 leading-relaxed">
          Ambas as partes se comprometem a manter a confidencialidade das informações técnicas, comerciais e pessoais trocadas durante a 
          vigência do contrato. Esta obrigação permanece válida por 5 anos após o término do contrato. A Hostul pode divulgar informações 
          quando exigido por lei, ordem judicial ou autoridade competente, sempre notificando o cliente quando possível.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">11. Foro e Jurisdição</h4>
        <p className="text-purple-200 leading-relaxed">
          Este contrato será regido pelas leis brasileiras. Qualquer controvérsia será resolvida no foro da comarca da sede da Hostul, 
          renunciando as partes a qualquer outro foro, por mais privilegiado que seja. As partes se comprometem a tentar resolver 
          disputas através de mediação antes de recorrer ao Poder Judiciário.
        </p>
      </div>
    </div>
  );

  const privacidadeContent = (
    <div className="space-y-6">
      <div className="bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-500 border-opacity-30">
        <p className="text-purple-200 text-sm">
          <strong>Última Revisão:</strong> 28/07/2025 13:39
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">1. Informações Coletadas</h4>
        <p className="text-purple-200 leading-relaxed">
          Coletamos informações pessoais como nome completo, e-mail, CPF/CNPJ, endereço residencial/comercial, telefone, 
          dados de pagamento (cartão de crédito, PIX, boleto) e informações de faturamento quando você utiliza nossos serviços. 
          Também coletamos dados técnicos como logs de acesso, informações de uso, endereços IP, cookies, dados de navegação, 
          informações do dispositivo, localização aproximada e métricas de performance para melhorar nossos serviços e 
          garantir a segurança da plataforma.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">2. Finalidade do Uso</h4>
        <p className="text-purple-200 leading-relaxed">
          Utilizamos suas informações para fornecer e melhorar nossos serviços, processar pagamentos e transações, enviar 
          comunicações importantes sobre serviços e atualizações, cumprir obrigações legais e regulamentares, prevenir 
          fraudes e atividades maliciosas, personalizar a experiência do usuário, realizar análises e pesquisas de mercado, 
          e fornecer suporte técnico. Não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros para 
          fins comerciais, exceto quando necessário para prestação dos serviços contratados ou por obrigação legal.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">3. Armazenamento e Segurança</h4>
        <p className="text-purple-200 leading-relaxed">
          Implementamos medidas de segurança técnicas e organizacionais de alto nível para proteger suas informações contra 
          acesso não autorizado, alteração, divulgação ou destruição. Utilizamos criptografia SSL/TLS de 256 bits, firewalls 
          de última geração, monitoramento 24/7, controle de acesso rigoroso com autenticação multifator, proteção contra 
          DDoS, backups criptografados e auditorias regulares de segurança. Os dados são armazenados em servidores seguros 
          localizados no Brasil, em conformidade com a LGPD e regulamentações de proteção de dados.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">4. Seus Direitos (LGPD)</h4>
        <p className="text-purple-200 leading-relaxed">
          Conforme a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018), você tem direito de acessar, corrigir, 
          excluir, portar, anonimizar ou bloquear seus dados pessoais, além do direito de revogar consentimento e ser 
          informado sobre compartilhamento de dados. Para exercer esses direitos, entre em contato conosco através dos 
          canais de suporte oficiais. Responderemos em até 15 dias úteis, podendo ser prorrogado por mais 15 dias em 
          casos complexos, sempre com justificativa.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">5. Cookies e Tecnologias Similares</h4>
        <p className="text-purple-200 leading-relaxed">
          Utilizamos cookies e tecnologias similares (web beacons, pixels, local storage) para melhorar a experiência do usuário, 
          analisar o uso do site, personalizar conteúdo, fornecer funcionalidades essenciais, manter sessões de usuário, 
          implementar recursos de segurança e otimizar o desempenho da plataforma. Você pode gerenciar suas preferências 
          de cookies através do navegador, de nossas configurações de privacidade no painel de controle ou através de 
          ferramentas de terceiros.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">6. Compartilhamento de Dados</h4>
        <p className="text-purple-200 leading-relaxed">
          Podemos compartilhar seus dados apenas com: prestadores de serviços essenciais (processadores de pagamento, 
          provedores de infraestrutura, serviços de e-mail), autoridades competentes quando exigido por lei ou ordem judicial, 
          parceiros comerciais com seu consentimento expresso para finalidades específicas, ou em caso de fusão, aquisição 
          ou venda de ativos da empresa. Todos os terceiros são obrigados a manter a confidencialidade e segurança dos dados.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">7. Retenção de Dados</h4>
        <p className="text-purple-200 leading-relaxed">
          Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades descritas nesta política, respeitando 
          obrigações legais, fiscais e contratuais. Após o término da relação contratual, os dados podem ser mantidos por até 
          5 anos para fins legais, fiscais, de auditoria e para cumprimento de obrigações regulamentares. Dados anonimizados 
          podem ser mantidos indefinidamente para fins de análise e melhoria de serviços.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">8. Transferência Internacional</h4>
        <p className="text-purple-200 leading-relaxed">
          Em casos específicos, seus dados podem ser transferidos para países que ofereçam nível adequado de proteção de dados 
          ou quando a transferência for necessária para prestação dos serviços. Tais transferências são realizadas em conformidade 
          com a LGPD e incluem salvaguardas adequadas para proteger seus dados pessoais.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">9. Alterações na Política</h4>
        <p className="text-purple-200 leading-relaxed">
          Podemos atualizar esta política periodicamente para refletir mudanças em nossas práticas, serviços ou requisitos legais. 
          Notificaremos sobre mudanças significativas através de e-mail, aviso em nosso site, painel de controle ou aplicativo 
          móvel com antecedência de 30 dias. O uso continuado dos serviços após as alterações constitui aceitação da nova política. 
          Recomendamos revisar esta política regularmente.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">10. Contato do Encarregado de Dados (DPO)</h4>
        <p className="text-purple-200 leading-relaxed">
          Para questões relacionadas à proteção de dados pessoais, exercício de direitos LGPD, dúvidas sobre esta política 
          ou reportar incidentes de segurança, entre em contato com nosso Encarregado de Dados (DPO) através do e-mail: 
          dpo@hostul.com.br. Nossa equipe especializada está preparada para atender suas solicitações e esclarecer dúvidas 
          sobre o tratamento de seus dados pessoais.
        </p>
      </div>
    </div>
  );

  const reembolsoContent = (
    <div className="space-y-6">
      <div className="bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-500 border-opacity-30">
        <p className="text-purple-200 text-sm">
          <strong>Última Revisão:</strong> 24/07/2026
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">1. Política de Reembolso</h4>
        <p className="text-purple-200 leading-relaxed">
          Para serviços pagos identificados como elegíveis no momento da contratação, a Hostul oferece reembolso integral a
          novos clientes nos primeiros 30 dias de contrato, desde que o cancelamento seja solicitado através dos canais oficiais
          e não haja violação dos termos de uso. Planos promocionais, gratuitos ou com desconto especial seguem as exceções
          descritas nesta política.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">2. Teste Gratuito e Ausência de Cobrança</h4>
        <p className="text-purple-200 leading-relaxed">
          O teste de 14 dias é gratuito, não exige cartão e não gera cobrança automática. Como não há pagamento durante o
          teste, seu encerramento não dá origem a reembolso. A política de reembolso passa a ser aplicável somente após uma
          contratação paga e de acordo com a elegibilidade e as exceções informadas nesta página.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">3. Condições para Reembolso</h4>
        <p className="text-purple-200 leading-relaxed">
          O reembolso será processado quando: o cliente solicitar cancelamento dentro dos primeiros 30 dias de contrato, 
          não houver violação dos termos de uso ou política de uso aceitável, o pagamento tenha sido realizado corretamente 
          e não seja um plano promocional, gratuito ou com desconto especial. O cliente deve ter utilizado os serviços 
          de forma adequada e não ter causado danos à infraestrutura ou outros usuários.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">4. Processo de Reembolso</h4>
        <p className="text-purple-200 leading-relaxed">
          O reembolso será processado em até 15 dias úteis após a aprovação da solicitação. Para pagamentos via cartão de crédito, 
          o valor será estornado na fatura do cartão utilizado. Para PIX e boleto, será feito transferência para a conta bancária 
          informada pelo cliente. O prazo para aparecer no extrato bancário pode variar conforme a instituição financeira. 
          O cliente receberá confirmação por e-mail quando o reembolso for processado.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">5. Casos de Não Reembolso</h4>
        <p className="text-purple-200 leading-relaxed">
          Não será oferecido reembolso para: cancelamentos após 30 dias de contrato, violação dos termos de uso ou política 
          de uso aceitável, planos promocionais, gratuitos ou com desconto especial, serviços de domínio (que são pagos 
          antecipadamente aos registradores e não são reembolsáveis), serviços de desenvolvimento personalizado já iniciados, 
          certificados SSL, serviços de backup adicional ou qualquer serviço de terceiros integrado à nossa plataforma.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">6. Reembolso por Indisponibilidade</h4>
        <p className="text-purple-200 leading-relaxed">
          Em caso de indisponibilidade que exceda o SLA contratado, será oferecido desconto proporcional na próxima fatura 
          conforme política de SLA, não sendo necessário solicitação do cliente. O desconto será calculado automaticamente 
          com base no tempo de indisponibilidade e aplicado na próxima cobrança. Para clientes que cancelarem o serviço, 
          o desconto será processado como reembolso proporcional.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">7. Reembolso Parcial</h4>
        <p className="text-purple-200 leading-relaxed">
          Em casos específicos, pode ser oferecido reembolso parcial quando: o cliente solicitar downgrade de plano, 
          houver problemas técnicos que impeçam o uso adequado dos serviços por período prolongado, ou quando a Hostul 
          não conseguir resolver problemas reportados dentro do prazo estabelecido no SLA. O valor do reembolso parcial 
          será calculado proporcionalmente ao período não utilizado.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">8. Cancelamento e Migração</h4>
        <p className="text-purple-200 leading-relaxed">
          Ao solicitar cancelamento, o cliente terá acesso aos serviços até o final do período contratado. Recomendamos 
          fazer backup de todos os dados antes do cancelamento. A Hostul oferece período de migração de 15 dias após o 
          cancelamento para que o cliente possa transferir seus dados para outro provedor. Após este período, os dados 
          podem ser excluídos permanentemente.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">9. Disputas e Reclamações</h4>
        <p className="text-purple-200 leading-relaxed">
          Em caso de disputa sobre reembolso, o cliente pode entrar em contato com nosso suporte técnico para esclarecimentos. 
          Todas as solicitações de reembolso são analisadas individualmente e podem ser negadas se houver evidências de 
          uso inadequado dos serviços. A decisão final sobre reembolso cabe à Hostul, sempre baseada em critérios objetivos 
          e documentados.
        </p>
      </div>
    </div>
  );

  const aceitavelContent = (
    <div className="space-y-6">
      <div className="bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-500 border-opacity-30">
        <p className="text-purple-200 text-sm">
          <strong>Última Revisão:</strong> 28/07/2025 13:39
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">1. Uso Aceitável dos Serviços</h4>
        <p className="text-purple-200 leading-relaxed">
          Os serviços da Hostul devem ser utilizados apenas para fins legais, legítimos e éticos. O cliente é responsável 
          por todo o conteúdo hospedado, atividades realizadas através de sua conta e conformidade com todas as leis aplicáveis. 
          Esta política estabelece diretrizes claras para garantir um ambiente seguro e confiável para todos os usuários.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">2. Conteúdo Proibido</h4>
        <p className="text-purple-200 leading-relaxed">
          É expressamente proibido hospedar, distribuir ou transmitir: conteúdo pornográfico ou adulto sem verificação de idade, 
          material ilegal ou que viole direitos autorais, spam em massa, malware, vírus, trojans ou qualquer software malicioso, 
          conteúdo que viole propriedade intelectual, atividades terroristas ou que promovam violência, discriminação, assédio 
          ou bullying, phishing, fraudes online, mineração de criptomoedas não autorizada, conteúdo que promova atividades 
          ilegais ou prejudique outros usuários.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">3. Limitações Técnicas</h4>
        <p className="text-purple-200 leading-relaxed">
          O cliente deve respeitar os limites de recursos contratados: espaço em disco, largura de banda mensal, processamento 
          de CPU, memória RAM, conexões simultâneas e número de domínios. Uso excessivo que prejudique outros usuários pode 
          resultar em throttling (redução temporária de velocidade), suspensão temporária ou permanente dos serviços. 
          Scripts que consumam recursos excessivos ou executem loops infinitos são estritamente proibidos.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">4. Segurança e Acesso</h4>
        <p className="text-purple-200 leading-relaxed">
          O cliente é responsável por manter a segurança de suas credenciais de acesso, não compartilhar senhas, usar senhas 
          fortes e únicas, manter sistemas e aplicações atualizados, implementar medidas de segurança adequadas, proteger 
          contra vulnerabilidades conhecidas, reportar imediatamente qualquer suspeita de comprometimento de segurança e 
          não tentar acessar sistemas ou dados de outros usuários.
        </p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold text-white mb-3">5. Conformidade Legal</h4>
        <p className="text-purple-200 leading-relaxed">
          O cliente deve cumprir todas as leis aplicáveis, incluindo LGPD (Lei Geral de Proteção de Dados), Marco Civil da Internet, 
          leis de direitos autorais, regulamentações específicas de seu setor de atuação, leis de proteção ao consumidor e 
          qualquer outra legislação relevante. A Hostul cooperará com autoridades competentes quando exigido por lei.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">6. Atividades de Rede</h4>
        <p className="text-purple-200 leading-relaxed">
          É proibido realizar ataques de negação de serviço (DDoS), port scanning não autorizado, tentativas de invasão, 
          exploração de vulnerabilidades, interceptação de tráfego de rede, spoofing de endereços IP, ou qualquer atividade 
          que possa comprometer a segurança da rede ou de outros usuários. O cliente deve reportar vulnerabilidades encontradas 
          através dos canais oficiais de segurança.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">7. E-mail e Comunicações</h4>
        <p className="text-purple-200 leading-relaxed">
          É proibido enviar spam, e-mails em massa não solicitados, phishing, spoofing de e-mail, ou usar nossos serviços 
          para distribuir malware através de e-mail. O cliente deve respeitar limites de envio de e-mail, implementar 
          autenticação SPF/DKIM/DMARC, manter listas de e-mail limpas e respeitar leis anti-spam. E-mails comerciais 
          devem incluir opção de descadastramento.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">8. Consequências de Violação</h4>
        <p className="text-purple-200 leading-relaxed">
          Violações desta política podem resultar em: suspensão temporária dos serviços (com notificação prévia quando possível), 
          suspensão permanente, rescisão contratual imediata, notificação às autoridades competentes, retenção de dados para 
          fins legais, cobrança de multas por danos causados, e proibição de uso futuro dos serviços. A severidade da 
          consequência será proporcional à gravidade da violação.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">9. Monitoramento e Relatórios</h4>
        <p className="text-purple-200 leading-relaxed">
          A Hostul monitora ativamente o uso dos serviços para detectar violações desta política, garantir a segurança 
          da infraestrutura e proteger outros usuários. O cliente pode reportar violações através dos canais de suporte. 
          Todas as denúncias são investigadas e tratadas com confidencialidade. A Hostul pode compartilhar informações 
          com autoridades quando necessário.
        </p>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-white mb-3">10. Revisão da Política</h4>
        <p className="text-purple-200 leading-relaxed">
          Esta política pode ser revisada periodicamente para refletir mudanças em ameaças de segurança, práticas da indústria 
          ou requisitos legais. Alterações significativas serão comunicadas com antecedência de 30 dias. O uso continuado 
          dos serviços após as modificações constitui aceitação da política atualizada. Recomendamos revisar esta política 
          regularmente.
        </p>
      </div>
    </div>
  );

  const legalSections = [
    {
      id: 'termos',
      title: 'Termos de Serviço',
      description: 'Condições gerais para utilização dos serviços da Hostul',
      icon: FileText,
      content: termosContent,
      version: TERMS_VERSION.termos
    },
    {
      id: 'contrato',
      title: 'Contrato de Hospedagem',
      description: 'Termos específicos para serviços de hospedagem web',
      icon: Shield,
      content: contratoContent,
      version: TERMS_VERSION.contrato
    },
    {
      id: 'privacidade',
      title: 'Política de Privacidade',
      description: 'Como coletamos, usamos e protegemos suas informações',
      icon: Users,
      content: privacidadeContent,
      version: TERMS_VERSION.privacidade
    },
    {
      id: 'reembolso',
      title: 'Política de Reembolso',
      description: 'Condições e procedimentos para reembolso',
      icon: BookOpen,
      content: reembolsoContent,
      version: TERMS_VERSION.reembolso
    },
    {
      id: 'aceitavel',
      title: 'Política de Uso Aceitável',
      description: 'Diretrizes para uso adequado dos serviços',
      icon: Shield,
      content: aceitavelContent,
      version: TERMS_VERSION.aceitavel
    }
  ];

  // Função para obter versões dos termos aceitos
  const getAcceptedTermsVersions = () => {
    return {
      termos: TERMS_VERSION.termos,
      contrato: TERMS_VERSION.contrato,
      privacidade: TERMS_VERSION.privacidade,
      reembolso: TERMS_VERSION.reembolso,
      aceitavel: TERMS_VERSION.aceitavel,
      dataAtualizacao: TERMS_VERSION.dataAtualizacao
    };
  };

  // Exportar versões para uso externo
  if (typeof window !== 'undefined') {
    window.HOSTUL_TERMS_VERSION = TERMS_VERSION;
    window.HOSTUL_GET_TERMS_VERSIONS = getAcceptedTermsVersions;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white overflow-x-hidden">
      <Header />
      
      <main className="pt-20 pb-0">
        {/* Hero Section */}
        <section className="pt-20 pb-24 md:pt-24 md:pb-32 relative overflow-hidden">
          {/* Efeito de partículas/grid no fundo */}
          <div className="absolute inset-0 z-0 opacity-20 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(#9f7aea_1px,transparent_1px)] [background-size:20px_20px]"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white border-opacity-20 shadow-2xl shadow-purple-500/10">
                <div className="flex flex-col items-center">
                  <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-4 rounded-2xl mb-6 shadow-lg shadow-purple-500/30">
                    <BookOpen size={32} className="text-white" />
                  </div>
                  <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
                    <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                      Documentos Legais
                    </span>
                  </h1>
                  <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl">
                    Transparência total em nossos termos e políticas
                  </p>
                  <div className="mt-6 w-16 h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"></div>
                </div>
              </div>
              
              {/* Cards informativos premium */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                <div className="bg-white bg-opacity-15 backdrop-filter backdrop-blur-lg rounded-xl p-6 border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20">
                  <div className="flex items-center mb-4">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                      <Shield size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Proteção Total</h3>
                  </div>
                  <p className="text-purple-100 text-sm leading-relaxed">
                    Seus dados protegidos por políticas rigorosas de segurança e privacidade
                  </p>
                </div>
                
                <div className="bg-white bg-opacity-15 backdrop-filter backdrop-blur-lg rounded-xl p-6 border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20">
                  <div className="flex items-center mb-4">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                      <FileText size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Termos Claros</h3>
                  </div>
                  <p className="text-purple-100 text-sm leading-relaxed">
                    Linguagem simples e direta. Sem surpresas, sem letras miúdas
                  </p>
                </div>
                
                <div className="bg-white bg-opacity-15 backdrop-filter backdrop-blur-lg rounded-xl p-6 border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20">
                  <div className="flex items-center mb-4">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                      <Users size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Suporte Jurídico</h3>
                  </div>
                  <p className="text-purple-100 text-sm leading-relaxed">
                    Equipe disponível para esclarecer dúvidas sobre termos e políticas
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Documents Section */}
        <section id="termos-politicas" className="pt-16 pb-16 bg-gradient-to-r from-purple-900 to-indigo-900 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="mb-12 text-center">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Termos e Políticas
                </h2>
                <p className="text-lg text-purple-200">
                  Selecione um documento para visualizar seu conteúdo completo
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Sumário Fixo - Lado Esquerdo */}
                <div className="lg:col-span-1">
                  <div 
                    className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl border border-white border-opacity-20 p-6 sticky top-8 max-h-[calc(100vh-8rem)] overflow-y-auto"
                    style={{ position: 'sticky', top: '2rem' }}
                  >
                    <h3 className="text-xl font-bold text-white mb-6">Documentos Disponíveis</h3>
                    <div className="space-y-3">
                      {legalSections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => navigateToSection(section.id)}
                          className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                            expandedSection === section.id
                              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                              : 'bg-white bg-opacity-10 text-purple-200 hover:bg-white hover:bg-opacity-20'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${
                              expandedSection === section.id 
                                ? 'bg-white bg-opacity-20' 
                                : 'bg-white bg-opacity-10'
                            }`}>
                              <section.icon size={20} className="text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm">{section.title}</h4>
                              <p className="text-xs opacity-80 mt-1">{section.description}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Conteúdo - Lado Direito */}
                <div className="lg:col-span-2">
                  <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl border border-white border-opacity-20 p-8 min-h-[600px]">
                    {expandedSection ? (
                      <div>
                        <div className="flex items-center mb-6">
                          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-3 rounded-lg mr-4">
                            {(() => {
                              const section = legalSections.find(s => s.id === expandedSection);
                              const IconComponent = section?.icon;
                              return IconComponent ? <IconComponent size={24} className="text-white" /> : null;
                            })()}
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-white">
                              {legalSections.find(s => s.id === expandedSection)?.title}
                            </h3>
                            <p className="text-purple-200 text-sm">
                              {legalSections.find(s => s.id === expandedSection)?.description}
                            </p>
                          </div>
                        </div>
                        <div className="prose prose-invert max-w-none text-sm leading-relaxed">
                          {legalSections.find(s => s.id === expandedSection)?.content}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="bg-white bg-opacity-10 p-6 rounded-2xl mb-6">
                          <BookOpen size={48} className="text-purple-200" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-4">
                          Selecione um Documento
                        </h3>
                        <p className="text-purple-200 max-w-md">
                          Escolha um documento no menu à esquerda para visualizar seu conteúdo completo e detalhado.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mt-16 bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl border border-white border-opacity-20 p-8">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Dúvidas sobre nossos termos?
                  </h3>
                  <p className="text-purple-200 mb-6">
                    Nossa equipe jurídica está disponível para esclarecer qualquer dúvida sobre nossos documentos legais.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/40 transition-all duration-300 transform hover:scale-105">
                      Falar com Suporte
                    </button>
                    <button className="border border-white border-opacity-30 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:bg-opacity-10 transition-all duration-300">
                      Enviar E-mail
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage; 
